import { useEffect, useState } from "react";
import { motion } from "@/lib/motion";
import { GitBranch, AlertTriangle, Layers } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { getHomepageTrust } from "../lib/sanityQueries";
import { peek } from "../lib/sanity";

const FALLBACK_CHALLENGES = [
    {
        title: "Hidden technology risk",
        description: "Security, access, documentation, and operational gaps often go unnoticed until a problem occurs — or growth exposes them.",
    },
    {
        title: "Lack of structure",
        description: "Onboarding, offboarding, access management, and technology governance are frequently inconsistent or undocumented.",
    },
    {
        title: "Reactive management",
        description: "Businesses spend too much time reacting to issues instead of proactively preventing them — turning technology into a bottleneck.",
    },
];

export const Challenges = () => {
    const [trust, setTrust] = useState(() => peek(getHomepageTrust));
    useEffect(() => {
        let active = true;
        getHomepageTrust()
            .then((data) => {
                if (active && data) setTrust(data);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    const challengeItems =
        trust?.challenges?.length ? trust.challenges : FALLBACK_CHALLENGES;
    const icons = [AlertTriangle, GitBranch, Layers];

    return (
    <section
        id="challenges"
        data-testid="challenges-section"
        className="relative mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36"
    >
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
                <Reveal>
                    <Overline>The Business Reality</Overline>
                </Reveal>
                <Reveal delay={0.05}>
                    <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight text-brand-text sm:text-5xl">
                        {trust?.trustHeadline ? (
                            trust.trustHeadline
                        ) : (
                            <>
                                Most businesses don't
                                <br />
                                lack technology —
                                <br />
                                <span className="text-brand-muted">they lack structure.</span>
                            </>
                        )}
                    </h2>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-6 max-w-md text-base leading-relaxed text-brand-muted">
                        {trust?.trustMessage ||
                            "As organizations grow, technology becomes more critical to operations, security, and productivity. Without the right processes, protection, and guidance, growth creates risk. We help you build the foundation to support it."}
                    </p>
                </Reveal>
            </div>

            <div className="lg:col-span-7">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-1">
                    {challengeItems.map((c, i) => {
                        const Icon = icons[i] || Layers;
                        return (
                        <Reveal key={c._key || c.title} delay={i * 0.08}>
                            <motion.div
                                whileHover={{ y: -4 }}
                                transition={{ duration: 0.3 }}
                                className="group flex items-start gap-5 rounded-2xl border border-brand-line bg-brand-surface p-6 transition-colors duration-150 hover:border-brand-accent/40 sm:p-7"
                            >
                                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border border-brand-line bg-brand-bg text-brand-accent transition-colors duration-150 group-hover:bg-brand-accent group-hover:text-brand-on-accent">
                                    <Icon className="h-5 w-5" />
                                </span>
                                <div>
                                    <span className="font-mono text-xs text-brand-muted">
                                        {`0${i + 1}`}
                                    </span>
                                    <h3 className="mt-1 font-heading text-xl font-bold tracking-tight text-brand-text">
                                        {c.title}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-brand-muted">
                                        {c.description}
                                    </p>
                                </div>
                            </motion.div>
                        </Reveal>
                        );
                    })}
                </div>
            </div>
        </div>
    </section>
    );
};
